/// Geometry for the Hand mark: a palm-up hand (🫴) holding a dot. The hand is Lucide's
/// `hand-helping` (ISC licence) moved down 3 units to make room for the dot, rewritten as absolute
/// cubics so the open and cupped poses share one command sequence and SVG `<animate>` can morph
/// between them without a motion library.

type Point = readonly [number, number];
/** Two control points and the end point; the start is the previous segment's end. */
type Segment = readonly [Point, Point, Point];

interface Subpath {
  start: Point;
  segments: Segment[];
}

/// Degrees the fingers turn toward the palm when the hand closes around the dot.
const CUP_DEGREES = 11;
const KNUCKLE: Point = [16.8, 17.8];
const FINGERTIP_CENTER: Point = [20.025, 11.945];

const lerp = (a: Point, b: Point, t: number): Point => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
];

function straight(from: Point, to: Point): Segment {
  return [lerp(from, to, 1 / 3), lerp(from, to, 2 / 3), to];
}

/// A circular arc as cubics, one per `parts`, from `fromDegrees` to `toDegrees` around `center`.
function arc(
  center: Point,
  radius: number,
  fromDegrees: number,
  toDegrees: number,
  parts: number,
): Segment[] {
  const step = (toDegrees - fromDegrees) / parts;
  return Array.from({ length: parts }, (_, part) => {
    const start = ((fromDegrees + step * part) * Math.PI) / 180;
    const end = ((fromDegrees + step * (part + 1)) * Math.PI) / 180;
    const reach = (4 / 3) * Math.tan((end - start) / 4) * radius;
    const from: Point = [
      center[0] + radius * Math.cos(start),
      center[1] + radius * Math.sin(start),
    ];
    const to: Point = [center[0] + radius * Math.cos(end), center[1] + radius * Math.sin(end)];
    return [
      [from[0] - reach * Math.sin(start), from[1] + reach * Math.cos(start)],
      [to[0] + reach * Math.sin(end), to[1] - reach * Math.cos(end)],
      to,
    ];
  });
}

function rotate(point: Point, pivot: Point, degrees: number): Point {
  const turn = (degrees * Math.PI) / 180;
  const dx = point[0] - pivot[0];
  const dy = point[1] - pivot[1];
  return [
    pivot[0] + dx * Math.cos(turn) - dy * Math.sin(turn),
    pivot[1] + dx * Math.sin(turn) + dy * Math.cos(turn),
  ];
}

const THUMB: Subpath = {
  start: [11, 15],
  segments: [
    straight([11, 15], [13, 15]),
    ...arc([13, 13], 2, 90, -90, 2),
    straight([13, 11], [10, 11]),
    [
      [9.4, 11],
      [8.9, 11.2],
      [8.6, 11.6],
    ],
    straight([8.6, 11.6], [3, 17]),
  ],
};

const CUFF: Subpath = { start: [2, 16], segments: [straight([2, 16], [8, 22])] };

/// Palm and fingers. Everything past the knuckle turns by `cupDegrees` to close around the dot.
function fingers(cupDegrees: number): Subpath {
  const palm: Segment[] = [
    straight([7, 21], [8.6, 19.6]),
    [
      [8.9, 19.2],
      [9.4, 19],
      [10, 19],
    ],
    straight([10, 19], [14, 19]),
    [[15.1, 19], [16.1, 18.6], KNUCKLE],
  ];
  const reach: Segment[] = [
    straight(KNUCKLE, [21.4, 13.4]),
    ...arc(FINGERTIP_CENTER, 2, 46.6, -133.4, 2),
    straight([18.65, 10.49], [14.45, 14.39]),
  ];
  const turn = (point: Point) => rotate(point, KNUCKLE, -cupDegrees);
  const curled = reach.map(([c1, c2, to]): Segment => [turn(c1), turn(c2), turn(to)]);
  return { start: [7, 21], segments: [...palm, ...curled] };
}

const round = (value: number) => Number(value.toFixed(2));
const at = (point: Point) => `${round(point[0])} ${round(point[1])}`;

function toPath(subpaths: Subpath[]): string {
  return subpaths
    .map(
      ({ start, segments }) =>
        `M ${at(start)} ${segments.map(([c1, c2, to]) => `C ${at(c1)} ${at(c2)} ${at(to)}`).join(" ")}`,
    )
    .join(" ");
}

const OPEN = toPath([THUMB, fingers(0), CUFF]);
const CUPPED = toPath([THUMB, fingers(CUP_DEGREES), CUFF]);

export const HAND_MARK_PATH = OPEN;
export const HAND_DOT = { cx: 14.8, restY: 8.3, aboveY: 1.2, r: 1.9 };

/// One loop: the dot drops into the open palm, the fingers close and the hand lifts it, then the
/// hand opens and lowers while the dot rises away. The dot has its own timeline because it lands
/// just before the fingers start to close.
const EASE = ".42 0 .58 1";
export const HAND_MORPH = {
  duration: "3.2s",
  hand: {
    keyTimes: "0;0.34;0.48;0.72;0.86;1",
    keySplines: Array(5).fill(EASE).join(";"),
    values: [OPEN, OPEN, CUPPED, CUPPED, OPEN, OPEN].join(";"),
    lift: "0 0;0 0;0 -1.2;0 -1.2;0 0;0 0",
  },
  dot: {
    keyTimes: "0;0.3;0.34;0.72;0.92;1",
    // Falls with gravity, settles, holds, then floats up and away.
    keySplines: `.55 0 .9 .6;.3 0 .7 1;${EASE};.4 0 .6 1;${EASE}`,
    y: [
      HAND_DOT.aboveY,
      HAND_DOT.restY - 0.3,
      HAND_DOT.restY,
      HAND_DOT.restY,
      HAND_DOT.aboveY,
      HAND_DOT.aboveY,
    ].join(";"),
    opacityTimes: "0;0.12;0.8;0.92;1",
    opacity: "0;1;1;0;0",
  },
};
